import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOption, OnyxTab } from '@onyx/angular';
import {
  OnyxDropdownComponent,
  OnyxIconComponent,
  OnyxSectionComponent,
  OnyxTabsComponent,
} from '@onyx/angular';
import { chain, isEqual, orderBy } from 'lodash';
import type { Country } from '../../../enums/country';
import { CountryGroup } from '../../../enums/country-group';
import { DictionaryCode } from '../../../enums/dictionary-code';
import { ValidationHelper } from '../../../helpers/validation.helper';
import { DataProvidersService } from '../../../services/data-providers.service';
import { DictionariesService } from '../../../services/dictionaries.service';

type CountriesWhitelistFormGroup = ReturnType<
  typeof CountriesWhitelistFormComponent.buildForm
>;

@Component({
  selector: 'app-countries-whitelist-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTabsComponent,
    OnyxDropdownComponent,
    TranslatePipe,
    OnyxIconComponent,
  ],
  templateUrl: './countries-whitelist-form.component.html',
  styleUrl: './countries-whitelist-form.component.scss',
})
export class CountriesWhitelistFormComponent implements OnInit {
  private readonly dataProvidersService = inject(DataProvidersService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<CountriesWhitelistFormGroup>();
  public readonly type = input.required<'vehicle' | 'trailer' | 'driver'>();
  public readonly licenseNumber = input<string | null>(null);
  public readonly permitNumber = input<string | null>(null);

  protected readonly I18N = 'forms.countriesWhitelist';

  protected readonly countryGroups = toSignal(
    this.dataProvidersService.getCountryGroups(),
  );
  private readonly countries = toSignal(
    this.dictionariesService.getDictionary(DictionaryCode.COUNTRY),
  );

  protected options = computed(() => {
    const countries = this.countries() ?? null;
    if (this.type() !== 'vehicle') return countries;

    return chain(countries)
      .map((country): OnyxOption<string> => {
        const isNational = this.nationalCountryCode() === country.value;
        const disabled = this.isCountryDisabled(isNational);

        return {
          ...country,
          disabled,
          rightIcon: disabled ? { name: 'clear-filled', size: 16 } : undefined,
          rightIconColor: disabled ? 'red' : 'gray',
          tooltip: this.getTooltip(isNational, disabled),
        };
      })
      .orderBy(({ disabled }) => disabled)
      .value();
  });

  protected countryGroupsTabs = computed(() => {
    const groups = this.countryGroups()
      ? Object.values(this.countryGroups()!)
      : null;
    if (!groups) return null;
    if (this.type() !== 'vehicle') return groups;

    return groups.map((group): OnyxTab<string> => {
      const isNational = group.value === CountryGroup.NATIONAL;
      const disabled = this.isCountryDisabled(isNational);

      return {
        ...group,
        disabled,
        tooltip: this.getTooltip(isNational, disabled),
      };
    });
  });

  private nationalCountryCode = computed(
    () => this.countryGroups()?.national?.countries[0],
  );

  public ngOnInit(): void {
    const controls = this.form().controls;
    const groupValue = toSignal(controls.group.valueChanges, {
      initialValue: controls.group.value,
      injector: this.injector,
    });
    const whitelistValue = toSignal(controls.whitelist.valueChanges, {
      initialValue: controls.whitelist.value,
      injector: this.injector,
    });

    effect(
      () => {
        const groups = this.countryGroups();
        const group = groupValue();
        if (!groups || !group) return;

        untracked(() =>
          controls.whitelist.setValue(
            this.getSortedCountries(groups[group].countries),
          ),
        );
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const groups = this.countryGroups();
        const whitelist = whitelistValue();
        if (!groups || !whitelist) return;

        const group =
          Object.values(groups).find((group) =>
            isEqual(this.getSortedCountries(group.countries), whitelist),
          )?.value ?? null;
        controls.group.setValue(group);
      },
      { injector: this.injector },
    );

    effect(
      () => {
        if (this.type() !== 'vehicle') return;

        this.licenseNumber();

        const nationalCode = this.nationalCountryCode();
        if (!whitelistValue().length && this.permitNumber() && nationalCode) {
          controls.whitelist.setValue([nationalCode]);
          return;
        }

        const enabledCountries = whitelistValue().filter(
          (code) => !this.isCountryDisabled(code === nationalCode),
        );
        if (enabledCountries.length !== whitelistValue().length) {
          controls.whitelist.setValue(
            this.getSortedCountries(enabledCountries),
          );
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        ValidationHelper.toggleControls(controls.whitelist, {
          enabled:
            this.countryGroupsTabs()?.some(({ disabled }) => !disabled) ??
            false,
          reset: false,
        });
      },
      { injector: this.injector },
    );
  }

  private getSortedCountries(countries: Country[]): Country[] {
    return orderBy(countries, (country) =>
      this.countries()?.findIndex(({ value }) => value === country),
    );
  }

  private isCountryDisabled(isNational: boolean): boolean {
    return !this.licenseNumber() && (!this.permitNumber() || !isNational);
  }

  private getTooltip(
    isNational: boolean,
    disabled: boolean,
  ): string | undefined {
    return disabled
      ? `${this.I18N}.${isNational ? 'missingPermit' : 'missingLicense'}`
      : undefined;
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      group: fb.control<CountryGroup | null>(null),
      whitelist: fb.control<Country[]>([], [Validators.required]),
    });
  }
}
